import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";

export const AUTOMATION_ACTION_KEY = {
  SEND_NOTIFICATION: "SendNotification",
  ASSIGNEE: "Assignee",
  STATUS: "Status",
  SEND_EMAIL: "SendEmail",
  CHANGE_STATUS: "ChangeStatus",
} as const;

export type AutomationActionKey =
  typeof AUTOMATION_ACTION_KEY[keyof typeof AUTOMATION_ACTION_KEY];

export const AUTOMATION_ACTION_KEYS = Object.values(AUTOMATION_ACTION_KEY);

export type AutomationActionModel = {
  actionName: string;
  actionKey: AutomationActionKey;
  id: string;
  relayId: string | null;
  createdAt: string;
  createdTimeUTC: string | null;
  editedTimeUTC: string | null;
};

export const AUTOMATION_CONDITION_KEY = {
  WORK_STATUS: "WorkStatus",
  WORK_REACHES_DATE: "WorkReachesDate",
  REACHED_DUE_DATE: "ReachedDueDate",
  PREVIOUS_SUBTASK_STATUS: "PrevioussSubTaskStatus",
  CURRENT_SUBTASK_STATUS: "CurrentSubtaskStatus",
  ALL_SUBTASKS_COMPLETED: "AllSubTasksCompleted",
  SUBTASK_REACHED_DUE_DATE: "SubTaskReachedDueDate",
} as const;

export type AutomationConditionKey =
  typeof AUTOMATION_CONDITION_KEY[keyof typeof AUTOMATION_CONDITION_KEY];

export const AUTOMATION_CONDITION_KEYS = Object.values(
  AUTOMATION_CONDITION_KEY,
);

export type AutomationConditionActionModel = {
  conditionId: string;
  actionId: string;
  action: AutomationActionModel;
  id: string;
  relayId: string | null;
  createdAt: string;
  createdTimeUTC: string | null;
  editedTimeUTC: string | null;
};

export type AutomationConditionModel = {
  conditionName: string;
  conditionKey: AutomationConditionKey;
  conditionAction: AutomationConditionActionModel[];
  id: string;
  relayId: string | null;
  createdAt: string;
  createdTimeUTC: string | null;
  editedTimeUTC: string | null;
};

export type AutomationJobTaskConditionActionModel = {
  conditionId: string;
  actionId: string;
  action: AutomationActionModel;
  id: string;
  relayId: string | null;
  createdAt: string;
  createdTimeUTC: string | null;
  editedTimeUTC: string | null;
};

export type AutomationJobTaskConditionModel = {
  conditionName: string;
  conditionKey: AutomationConditionKey;
  jobTaskConditionAction: AutomationJobTaskConditionActionModel[];
  id: string;
  relayId: string | null;
  createdAt: string;
  createdTimeUTC: string | null;
  editedTimeUTC: string | null;
};

export type AutomationStatusData = {
  mainStatusId?: string | null;
  subStatusId?: string | null;
};

export type AutomationDateConditionValue = {
  direction: "before" | "after";
  days: number;
};

export type AutomationAssigneeData = {
  userIds: Array<string | null>;
};

export type AutomationTemplateData = {
  emailTemplateId?: string | null;
  emailAddress?: string | null;
};

export type AutomationValue =
  | string
  | string[]
  | AutomationStatusData
  | AutomationDateConditionValue
  | AutomationAssigneeData
  | AutomationTemplateData
  | null;

export type AutomationConditionValue = AutomationValue;
export type AutomationActionValue = Exclude<
  AutomationValue,
  AutomationDateConditionValue
>;

export type AutomationSerializedValue = string | null;

export type AddAutomationPayload = {
  orgId: string;
  createdById: string;
  conditionId: string;
  actionId: string;
  isActive: boolean;
  isExecuted: boolean;
  isSuccess: boolean;
  conditionValue: AutomationConditionValue;
  actionValue: AutomationActionValue;
  noOfDays?: number;
};

export type AddWorkAutomationPayload = AddAutomationPayload & {
  workId: string;
};

export type AddJobTaskAutomationPayload = AddAutomationPayload & {
  jobTaskId: string;
};

export type DefaultAutomationBasePayload = Omit<
  AddAutomationPayload,
  "createdById" | "isExecuted" | "isSuccess"
>;

export type DefaultWorkAutomationPayload = DefaultAutomationBasePayload & {
  workTypeId: string;
};

export type DefaultJobTaskAutomationPayload = DefaultAutomationBasePayload & {
  templateTaskId: string;
};

export type AutomationUserModel = {
  id: string;
  userProfile?: {
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    email?: string | null;
    color?: string | null;
    userId?: string | null;
    id?: string | null;
  } | null;
};

export type AutomationWorkModel = {
  id: string;
  name?: string | null;
  description?: string | null;
  timeBudget?: number | null;
};

export type AutomationWorkTaskModel = {
  id: string;
  name?: string | null;
  description?: string | null;
  timeBudget?: number | null;
  jobId?: string | null;
};

export type WorkAutomationModel = {
  workId: string;
  orgId: string;
  createdById: string;
  isActive: boolean;
  isExecuted: boolean;
  isSuccess: boolean;
  conditionId: string;
  actionId: string;
  conditionValue: AutomationSerializedValue;
  noOfDays: number | null;
  actionValue: AutomationSerializedValue;
  executedOn: string | null;
  work: AutomationWorkModel | null;
  createdBy: AutomationUserModel | null;
  condition: AutomationConditionModel | null;
  action: AutomationActionModel | null;
  id: string;
  relayId: string | null;
  createdAt: string;
  createdTimeUTC: string | null;
  editedTimeUTC: string | null;
};

export type JobTaskAutomationModel = {
  jobTaskId: string;
  orgId: string;
  createdById: string;
  isActive: boolean;
  isExecuted: boolean;
  isSuccess: boolean;
  conditionId: string;
  actionId: string;
  conditionValue: AutomationSerializedValue;
  noOfDays: number | null;
  actionValue: AutomationSerializedValue;
  executedOn: string | null;
  workTask?: AutomationWorkTaskModel | null;
  jobTask?: AutomationWorkTaskModel | null;
  createdBy: AutomationUserModel | null;
  condition: AutomationJobTaskConditionModel | null;
  action: AutomationActionModel | null;
  id: string;
  relayId: string | null;
  createdAt: string;
  createdTimeUTC: string | null;
  editedTimeUTC: string | null;
};

export type GetAutomationsResponse = {
  workAutomations: WorkAutomationModel[];
  workTaskAutomations: JobTaskAutomationModel[];
};

export const automationSlice = createApi({
  reducerPath: "automationSlice",
  tagTypes: ["Automation", "AutomationAction", "AutomationCondition"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getAutomations: build.query<GetAutomationsResponse, { workId: string }>({
      query: ({ workId }) => ({
        url: "api/automation/get-all",
        params: { workId },
        method: "GET",
      }),
      keepUnusedDataFor: 0,
      providesTags: ["Automation"],
    }),
    getAutomation: build.query<
      WorkAutomationModel | JobTaskAutomationModel,
      string
    >({
      query: (id) => ({
        url: "api/automation/id",
        params: { id },
        method: "GET",
      }),
      providesTags: ["Automation"],
    }),
    addAutomation: build.mutation<unknown, AddWorkAutomationPayload>({
      query: (body) => ({
        url: "api/automation/add",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Automation"],
    }),
    addJobTaskAutomation: build.mutation<unknown, AddJobTaskAutomationPayload>({
      query: (body) => ({
        url: "api/jobtask-automation/add",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Automation"],
    }),
    addDefaultAutomation: build.mutation<unknown, DefaultWorkAutomationPayload>(
      {
        query: (body) => ({
          url: "api/automation/add-default",
          body,
          method: "POST",
        }),
        invalidatesTags: ["Automation"],
      },
    ),
    addDefaultJobTaskAutomation: build.mutation<
      unknown,
      DefaultJobTaskAutomationPayload
    >({
      query: (body) => ({
        url: "api/jobtask-automation/add-default",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Automation"],
    }),
    updateAutomation: build.mutation<unknown, Record<string, unknown>>({
      query: (body) => ({
        url: "api/automation/update",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Automation"],
    }),
    deleteAutomation: build.mutation<unknown, string>({
      query: (id) => ({
        url: "api/automation",
        params: { id },
        method: "DELETE",
      }),
      invalidatesTags: ["Automation"],
    }),
    deleteJobTaskAutomation: build.mutation<unknown, string>({
      query: (id) => ({
        url: "api/jobtask-automation",
        params: { id },
        method: "DELETE",
      }),
      invalidatesTags: ["Automation"],
    }),
    deleteDefaultAutomation: build.mutation<unknown, string>({
      query: (defaultAutomationId) => ({
        url: "api/automation/default",
        params: { defaultAutomationId },
        method: "DELETE",
      }),
      invalidatesTags: ["Automation"],
    }),
    deleteDefaultJobTaskAutomation: build.mutation<unknown, string>({
      query: (defaultAutomationId) => ({
        url: "api/jobtask-automation/default",
        params: { defaultAutomationId },
        method: "DELETE",
      }),
      invalidatesTags: ["Automation"],
    }),
    getAutomationActions: build.query<AutomationActionModel[], void>({
      query: () => ({
        url: "api/automation-action/get-all",
        method: "GET",
      }),
      providesTags: ["AutomationAction"],
    }),
    getAutomationJobTaskActions: build.query<unknown[], void>({
      query: () => ({
        url: "api/automation-action/get-all-jobtask",
        method: "GET",
      }),
      providesTags: ["AutomationAction"],
    }),
    getAutomationConditions: build.query<AutomationConditionModel[], void>({
      query: () => ({
        url: "api/automation-condition/get-all",
        method: "GET",
      }),
      providesTags: ["AutomationCondition"],
    }),
    getAutomationJobTaskConditions: build.query<
      AutomationJobTaskConditionModel[],
      void
    >({
      query: () => ({
        url: "api/automation-condition/get-all-jobtask",
        method: "GET",
      }),
      providesTags: ["AutomationCondition"],
    }),
  }),
});

export const {
  useGetAutomationsQuery,
  useLazyGetAutomationsQuery,
  useLazyGetAutomationQuery,
  useAddAutomationMutation,
  useAddJobTaskAutomationMutation,
  useAddDefaultAutomationMutation,
  useAddDefaultJobTaskAutomationMutation,
  useUpdateAutomationMutation,
  useDeleteAutomationMutation,
  useDeleteJobTaskAutomationMutation,
  useDeleteDefaultAutomationMutation,
  useDeleteDefaultJobTaskAutomationMutation,

  useGetAutomationActionsQuery,
  useLazyGetAutomationActionsQuery,
  useGetAutomationJobTaskActionsQuery,
  useLazyGetAutomationJobTaskActionsQuery,

  useGetAutomationConditionsQuery,
  useLazyGetAutomationConditionsQuery,
  useGetAutomationJobTaskConditionsQuery,
  useLazyGetAutomationJobTaskConditionsQuery,
} = automationSlice;
